"use client";

import { useMemo, useState } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { BookOpen, ClipboardPlus, Server, FileText, Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { fetchData } from "@/utils/api-handler";
import { AgentKnowledgeBaseUpload } from "@/components/shared/agent-knowledge-base-upload";
import { PermissionGate } from "@/components/shared/permission-gate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SortableColumn } from "@/components/shared/sortable-column";
import { DataTable } from "@/components/shared/data-table";
import TablePagination from "@/components/shared/table-pagination";
import { usePersistedPage } from "@/hooks/use-persisted-page";
import { ColumnDef } from "@tanstack/react-table";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { getDocumentStatusColor, normalizeAttachedDatasources } from "@/utils/documents";
import { SEARCH_DEBOUNCE_WAIT } from "@/utils/global";
import { DocumentsActionCell } from "@/components/documents";

function KnowledgeBaseListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-2">
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-4 rounded-xl border bg-card p-3 sm:p-4"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Skeleton className="h-10 w-10 shrink-0 rounded-[9px]" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-36 sm:w-48" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

interface AgentKnowledgeBaseTabProps {
  className?: string;
  description?: string;
  title?: string;
  datasources?: AgentDatasourceInput[];
  tableContainerClassName?: string;
}

interface DatasourceDocumentsProps {
  datasource: { id: string; name: string; updated_at?: string };
  onBack: () => void;
  onAddDocuments: () => void;
  tableContainerClassName?: string;
}

function DatasourceDocuments({
  datasource,
  onBack,
  onAddDocuments,
  tableContainerClassName,
}: DatasourceDocumentsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = usePersistedPage("kb-page");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (col: string) => {
    setSortBy(col);
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const { data, isLoading, hasPermission } = useProtectedQuery<DocumentFilesResponse>({
    queryKey: ["kb-docs", datasource.id, debouncedSearch, currentPage, sortBy, sortOrder, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        datasource_id: datasource.id,
        page: currentPage.toString(),
        page_size: "10",
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (statusFilter !== "all") params.set("status", statusFilter);
      return fetchData(`/api/documents/files?${params.toString()}`);
    },
    requiredPermissions: ["LIST_DOCUMENTS"],
  });

  const columns: ColumnDef<DocumentFile>[] = [
    {
      accessorKey: "name",
      header: () => (
        <SortableColumn
          label="Name"
          columnKey="name"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-start gap-3 pl-4">
          <FileText className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <p className="font-medium truncate">{row.getValue("name")}</p>
        </div>
      ),
    },
    {
      accessorKey: "type",
      header: () => <div className="inline-flex items-center h-9 px-4">Type</div>,
      cell: ({ row }) => {
        const type = (row.getValue("type") as string)?.toUpperCase();
        return (
          <div className="pl-4">
            <Badge variant="outline" className="font-mono">
              {type || "—"}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: () => <div className="inline-flex items-center h-9 px-4">Status</div>,
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return (
          <div className="pl-4">
            <Badge className={getDocumentStatusColor(status)}>{status}</Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: () => (
        <SortableColumn
          label="Created At"
          columnKey="created_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue("created_at"));
        return (
          <div className="flex flex-col pl-4">
            <span>{format(date, "MMM d, yyyy")}</span>
            <span className="text-muted-foreground text-sm">{format(date, "hh:mm a")}</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <DocumentsActionCell
          row={row}
          invalidateQueryKeys={[["kb-docs"], ["documents-files"]]}
        />
      ),
    },
  ];

  if (!hasPermission) {
    return <AccessDeniedCard message="You don't have permission to view documents." />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="shrink-0"
            aria-label="Back to knowledge bases"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
            <Server className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-bold sm:text-xl">{datasource.name}</h3>
            {datasource.updated_at && (
              <p className="text-sm text-muted-foreground">
                Last updated {format(new Date(datasource.updated_at), "MMM d, yyyy")}
              </p>
            )}
          </div>
          <PermissionGate requiredPermissions={["CREATE_DOCUMENT"]}>
            <Button type="button" onClick={onAddDocuments} className="shrink-0">
              <ClipboardPlus className="mr-1 h-4 w-4" />
              Add documents
            </Button>
          </PermissionGate>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            className="w-full pl-8 sm:flex-1"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startIcon={
              <Search className="absolute top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            }
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PROCESSED">Processed</SelectItem>
              <SelectItem value="UPLOADED">Uploaded</SelectItem>
              <SelectItem value="UPLOADING">Uploading</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-lg border bg-card">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            <DataTable
              columns={columns}
              data={data?.documents ?? []}
              containerClassName={tableContainerClassName}
            />
          </div>
        </div>
      </div>

      <TablePagination
        currentPage={currentPage}
        pageCount={data?.page_count ?? 1}
        onPageChange={setCurrentPage}
        pageParamName="kb-page"
      />
    </div>
  );
}

interface AttachedDatasourcesViewProps {
  className?: string;
  description: string;
  title: string;
  datasources: AttachedDatasource[];
  tableContainerClassName: string;
}

function AttachedDatasourcesView({
  className,
  description,
  title,
  datasources,
  tableContainerClassName,
}: AttachedDatasourcesViewProps) {
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDatasource, setSelectedDatasource] = useState<AttachedDatasource | null>(null);
  const [search, setSearch] = useState("");

  const { data: dsData, isLoading } = useProtectedQuery<{
    datasources: Array<{ id: string; name: string; updated_at: string; description?: string }>;
  }>({
    queryKey: ["datasources-with-groups"],
    queryFn: () => fetchData("/api/documents/datasources/all"),
    requiredPermissions: ["LIST_DATA_SOURCES"],
    enabled: datasources.length > 0,
  });

  const displayDatasources = useMemo(() => {
    const catalog = dsData?.datasources ?? [];
    return datasources.map((datasource) => {
      const match = catalog.find((item) => item.id === datasource.id);
      const hasDisplayName = datasource.name && datasource.name !== datasource.id;

      return {
        ...datasource,
        name: hasDisplayName ? datasource.name : (match?.name ?? datasource.name),
        updated_at: datasource.updated_at ?? match?.updated_at,
        description: datasource.description ?? match?.description ?? null,
      };
    });
  }, [datasources, dsData]);

  const allowedDatasourceIds = datasources.map((ds) => ds.id);

  const filtered = displayDatasources.filter((ds) =>
    ds.name.toLowerCase().includes(search.toLowerCase())
  );

  if (selectedDatasource && !showUpload) {
    return (
      <div className={cn("py-6", className)}>
        <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm">
          <CardContent className="p-5 sm:p-6">
            <DatasourceDocuments
              datasource={selectedDatasource}
              onBack={() => setSelectedDatasource(null)}
              onAddDocuments={() => setShowUpload(true)}
              tableContainerClassName={tableContainerClassName}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("py-6", className)}>
      <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm">
        <CardHeader className="border-b px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
                <BookOpen className="size-5 text-primary" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-xl">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
            </div>
            {!showUpload && (
              <PermissionGate requiredPermissions={["CREATE_DOCUMENT"]}>
                <Button type="button" onClick={() => setShowUpload(true)} className="shrink-0">
                  <ClipboardPlus className="mr-1 h-4 w-4" />
                  Add documents
                </Button>
              </PermissionGate>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {showUpload && (
            <AgentKnowledgeBaseUpload
              onCancel={() => setShowUpload(false)}
              allowedDatasourceIds={allowedDatasourceIds}
              initialDatasourceId={selectedDatasource?.id}
            />
          )}

          {!showUpload && (
            <div className="space-y-4 p-5 sm:p-6">
              <Input
                className="w-full pl-8 sm:max-w-xs"
                placeholder="Search attached knowledge bases..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={
                  <Search className="absolute top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                }
              />

              {isLoading ? (
                <KnowledgeBaseListSkeleton />
              ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed py-12 text-center text-muted-foreground">
                  <Server className="mx-auto mb-3 h-10 w-10 opacity-30" />
                  <p className="text-sm font-medium text-foreground">No knowledge bases attached</p>
                  <p className="mt-1 text-sm">
                    Attach knowledge bases in agent settings to browse documents here.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {filtered.map((ds) => (
                    <button
                      key={ds.id}
                      type="button"
                      onClick={() => setSelectedDatasource(ds)}
                      className="group flex items-center justify-between gap-4 rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/30 sm:p-4"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
                          <Server className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium group-hover:text-primary transition-colors">
                            {ds.name}
                          </p>
                          {ds.updated_at && (
                            <p className="text-xs text-muted-foreground">
                              Updated {format(new Date(ds.updated_at), "MMM d, yyyy")}
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function AgentKnowledgeBaseTab({
  className,
  description,
  title = "Knowledge base",
  datasources,
  tableContainerClassName = "max-h-[calc(100dvh-420px)] sm:max-h-[calc(88vh-280px)]",
}: AgentKnowledgeBaseTabProps) {
  const normalizedDatasources = normalizeAttachedDatasources(datasources);

  return (
    <AttachedDatasourcesView
      className={className}
      description={
        description ??
        "Browse knowledge bases attached to this agent and their documents."
      }
      title={title}
      datasources={normalizedDatasources}
      tableContainerClassName={tableContainerClassName}
    />
  );
}
